import JSZip from 'jszip'
import { parseOpfDocument, type ParsedEpub } from './metadata'

/**
 * Parser de EPUB.
 *
 * Un EPUB es un zip con una estructura estandarizada:
 *
 *   META-INF/container.xml   → indica dónde está el OPF raíz
 *   OEBPS/content.opf        → metadatos (dc:title, dc:creator…) + manifest
 *   OEBPS/…                  → contenido (xhtml, imágenes…)
 *
 * Este módlo lee esa estructura con JSZip y devuelve metadatos +
 * portada como Blob. Es async porque JSZip descomprime en Web Workers.
 */

export interface ParseResult extends ParsedEpub {
  /** Blob de la portada, o null si el libro no declara ninguna. */
  coverBlob: Blob | null
}

/** Error lanzado cuando el archivo no es un EPUB válido. */
export class InvalidEpubError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidEpubError'
  }
}

const domParser = new DOMParser()

/**
 * Lee metadatos y portada de un archivo EPUB.
 * @throws {InvalidEpubError} si el zip no tiene la estructura mínima.
 */
export async function parseEpub(file: Blob): Promise<ParseResult> {
  const zip = await openZip(file)

  const opfPath = await findOpfPath(zip)
  const opfText = await readText(zip, opfPath)
  const doc = domParser.parseFromString(opfText, 'application/xml')

  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new InvalidEpubError(`OPF mal formado en "${opfPath}"`)
  }

  const parsed = parseOpfDocument(doc)

  const coverBlob = parsed.coverHref
    ? await readCover(zip, parsed.coverHref, opfPath)
    : null

  return { ...parsed, coverBlob }
}

// ---------- Internos exportados (compartidos con extract-text.ts) ----------

/** Abre el zip del EPUB; lanza InvalidEpubError si no es un zip válido. */
export async function openZip(file: Blob): Promise<JSZip> {
  try {
    return await JSZip.loadAsync(file)
  } catch {
    throw new InvalidEpubError('El archivo no es un zip válido (¿no es un EPUB?).')
  }
}

/**
 * Localiza el OPF raíz leyendo META-INF/container.xml.
 * El container declara `<rootfile full-path="OEBPS/content.opf"/>`.
 */
export async function findOpfPath(zip: JSZip): Promise<string> {
  const containerFile = zip.file('META-INF/container.xml')
  if (!containerFile) {
    throw new InvalidEpubError('Falta META-INF/container.xml: no es un EPUB estándar.')
  }
  const containerText = await containerFile.async('text')
  const doc = domParser.parseFromString(containerText, 'application/xml')

  const rootfile = doc.getElementsByTagName('rootfile')[0]
  const fullPath = rootfile?.getAttribute('full-path')
  if (!fullPath || !zip.file(fullPath)) {
    throw new InvalidEpubError('container.xml no declara un rootfile válido.')
  }
  return fullPath
}

/** Lee un archivo del zip como texto UTF-8. */
export async function readText(zip: JSZip, path: string): Promise<string> {
  const entry = zip.file(path)
  if (!entry) throw new InvalidEpubError(`No se encontró "${path}" en el EPUB.`)
  return entry.async('text')
}

/**
 * Carga la portada resolviendo su href relativo al OPF.
 * El href del OPF es relativo a la ubicación del propio OPF dentro del zip,
 * así que hay que resolverlo contra el directorio del OPF.
 */
async function readCover(zip: JSZip, coverHref: string, opfPath: string): Promise<Blob | null> {
  const absCoverPath = resolveRelativePath(coverHref, opfPath)

  // Algunos OPF declaran el href con fragmento (#section) o query; limpiar.
  const cleanPath = absCoverPath.split('#')[0]?.split('?')[0]
  if (!cleanPath) return null

  const entry = zip.file(cleanPath)
  if (!entry) {
    // No es fatal: un libro puede referenciar una portada inexistente.
    return null
  }

  // JSZip puede devolver el Blob directamente si pedimos 'blob'.
  return entry.async('blob')
}

/**
 * Resuelve `relative` contra el directorio de `base`.
 * Ej: ("images/cover.jpg", "OEBPS/content.opf") → "OEBPS/images/cover.jpg"
 */
export function resolveRelativePath(relative: string, base: string): string {
  const baseDir = base.includes('/') ? base.slice(0, base.lastIndexOf('/') + 1) : ''
  const combined = baseDir + relative

  // Normaliza segmentos "./" y "../"
  const segments = combined.split('/')
  const out: string[] = []
  for (const seg of segments) {
    if (seg === '.' || seg === '') continue
    if (seg === '..') {
      out.pop()
      continue
    }
    out.push(seg)
  }
  return out.join('/')
}
